import React, { useState } from 'react';
import { TrendingUp, Users, Euro, Check, Shield, Zap, BarChart3, Wallet, Eye, EyeOff, Loader2, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const BecomeVendor = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    password: '',
    confirmPassword: '',
    motivation: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    if (formData.password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    setLoading(true);

    try {
      await api.post('/vendors/register', {
        email: formData.email,
        full_name: formData.full_name,
        password: formData.password,
        motivation: formData.motivation
      });

      setSuccess(true);
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  const commissionTiers = [
    { sales: '0 à 9 ventes', commission: '10 €', color: '#FFB800' },
    { sales: '10 à 19 ventes', commission: '15 €', color: '#FF6B35' },
    { sales: '20 à 39 ventes', commission: '20 €', color: '#F7931E' },
    { sales: '40 ventes et +', commission: '25 €', color: '#00FF88' }
  ];

  const benefits = [
    { icon: <Check className="h-5 w-5" />, text: 'Une offre facile à expliquer' },
    { icon: <Check className="h-5 w-5" />, text: 'Un système de commissions clair' },
    { icon: <Check className="h-5 w-5" />, text: 'Un espace vendeur structuré' },
    { icon: <Check className="h-5 w-5" />, text: 'Un accompagnement pensé pour durer' }
  ];

  const whySell = [
    { icon: <Zap className="h-6 w-6" />, title: 'Un seul abonnement', desc: 'Pas de gamme compliquée' },
    { icon: <Euro className="h-6 w-6" />, title: 'Un seul prix', desc: '59€/an, clair et simple' },
    { icon: <Users className="h-6 w-6" />, title: 'Pour toute la famille', desc: 'Sport, films, séries, TV' },
    { icon: <Shield className="h-6 w-6" />, title: 'Compatible partout', desc: 'Tous les écrans' }
  ];

  const notYourJob = [
    'L\'accès client',
    'La plateforme IPTV',
    'Les tutoriels',
    'Le support technique',
    'La facturation'
  ];

  return (
    <div className="min-h-screen py-20" data-testid="become-vendor-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="inline-block p-4 bg-[#FF6B35]/10 rounded-full mb-6">
            <TrendingUp className="h-12 w-12 text-[#FF6B35]" />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            Devenir Vendeur IPTV4KFR
          </h1>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#FF6B35] mb-6">
            Rejoignez la team IPTV4KFR
          </h2>
          <p className="text-base sm:text-lg text-[#B8B8BA] max-w-3xl mx-auto mb-8">
            Développez des revenus récurrents en recommandant une offre simple, premium et très demandée.
          </p>

          {/* Benefits Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto mb-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-[#1A1A1D] to-[#121214] p-4 rounded-lg border border-white/10 flex items-center space-x-3"
              >
                <div className="text-[#00FF88]">{benefit.icon}</div>
                <span className="text-white text-sm">{benefit.text}</span>
              </div>
            ))}
          </div>

          <div className="bg-[#FF6B35]/10 border border-[#FF6B35]/30 p-6 rounded-xl max-w-3xl mx-auto">
            <p className="text-white text-lg mb-2">
              👉 <strong>Vous vous concentrez sur la recommandation.</strong>
            </p>
            <p className="text-[#FF6B35] font-semibold text-lg">
              👉 La plateforme gère le reste.
            </p>
          </div>
        </motion.div>

        {/* Pourquoi devenir vendeur */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-16"
        >
          <h2 className="text-3xl font-bold text-white mb-8 text-center">
            Pourquoi devenir vendeur IPTV4KFR ?
          </h2>

          <div className="bg-gradient-to-br from-[#1A1A1D] to-[#121214] p-8 rounded-xl border border-white/10 mb-8">
            <h3 className="text-2xl font-bold text-white mb-6">
              Une offre qui se vend naturellement
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {whySell.map((item, index) => (
                <div key={index} className="text-center">
                  <div className="text-[#FF6B35] mb-3 flex justify-center">
                    {item.icon}
                  </div>
                  <h4 className="text-white font-bold mb-2">{item.title}</h4>
                  <p className="text-[#B8B8BA] text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
            <p className="text-center text-[#00FF88] mt-6 font-semibold">
              👉 Pas de discours complexe, pas de technique à maîtriser.
            </p>
          </div>

          {/* Commissions */}
          <div className="bg-gradient-to-br from-[#1A1A1D] to-[#121214] p-8 rounded-xl border border-white/10 mb-8">
            <h3 className="text-2xl font-bold text-white mb-4 text-center">
              Des commissions attractives et évolutives
            </h3>
            <p className="text-[#B8B8BA] text-center mb-6">
              Vos revenus augmentent en fonction de vos performances mensuelles.
            </p>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#3A3A3D]">
                    <th className="text-left py-4 px-6 text-[#B8B8BA] font-semibold">Ventes mensuelles (M-1)</th>
                    <th className="text-right py-4 px-6 text-[#B8B8BA] font-semibold">Commission par vente</th>
                  </tr>
                </thead>
                <tbody>
                  {commissionTiers.map((tier, index) => (
                    <tr key={index} className="border-b border-[#3A3A3D]/50 hover:bg-[#222226]/50 transition-colors">
                      <td className="py-4 px-6 text-white">{tier.sales}</td>
                      <td className="py-4 px-6 text-right">
                        <span className="font-bold text-xl" style={{ color: tier.color }}>
                          {tier.commission}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-[#6B6B6E] text-sm text-center mt-4">
              * Les paliers sont calculés chaque mois sur la base du mois précédent (M-1).
            </p>
          </div>

          {/* Bonus parrainage */}
          <div className="bg-gradient-to-br from-[#FF6B35]/10 to-[#F7931E]/10 p-6 rounded-xl border border-[#FF6B35]/30 mb-8">
            <div className="flex items-center space-x-3 mb-4">
              <Users className="h-6 w-6 text-[#FF6B35]" />
              <h3 className="text-xl font-bold text-white">Un bonus parrainage intégré</h3>
            </div>
            <p className="text-[#E8E8E8] mb-3">
              Lorsqu'un de vos clients parraine un nouvel abonné :
            </p>
            <div className="bg-[#00FF88]/10 border border-[#00FF88]/30 p-4 rounded-lg inline-block">
              <p className="text-[#00FF88] font-bold text-lg">
                Vous recevez 5€ supplémentaires
              </p>
            </div>
            <p className="text-[#FF6B35] mt-4 font-semibold">
              👉 Vos clients deviennent aussi des ambassadeurs.
            </p>
          </div>
        </motion.div>

        {/* Espace vendeur dédié */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-16"
        >
          <div className="bg-gradient-to-br from-[#1A1A1D] to-[#121214] p-8 rounded-xl border border-white/10">
            <div className="flex items-center space-x-3 mb-6">
              <BarChart3 className="h-6 w-6 text-[#FFB800]" />
              <h3 className="text-2xl font-bold text-white">Un espace vendeur dédié</h3>
            </div>
            <p className="text-[#B8B8BA] mb-6">
              Depuis votre dashboard, vous pouvez :
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start space-x-3">
                <Eye className="h-5 w-5 text-[#00FF88] mt-1 flex-shrink-0" />
                <div>
                  <p className="text-white font-semibold">Suivre vos ventes en temps réel</p>
                  <p className="text-[#6B6B6E] text-sm">Dashboard complet et actualisé</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Euro className="h-5 w-5 text-[#FFB800] mt-1 flex-shrink-0" />
                <div>
                  <p className="text-white font-semibold">Visualiser vos commissions</p>
                  <p className="text-[#6B6B6E] text-sm">Disponibles, en attente, retirées</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Wallet className="h-5 w-5 text-[#FF6B35] mt-1 flex-shrink-0" />
                <div>
                  <p className="text-white font-semibold">Consulter votre solde disponible</p>
                  <p className="text-[#6B6B6E] text-sm">Suivi transparent de vos gains</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-5 w-5 text-[#00FFFF] mt-1 flex-shrink-0" />
                <div>
                  <p className="text-white font-semibold">Demander vos paiements simplement</p>
                  <p className="text-[#6B6B6E] text-sm">Process clair, sans complexité</p>
                </div>
              </div>
            </div>

            <div className="mt-6 bg-[#00FF88]/10 border border-[#00FF88]/30 p-4 rounded-lg">
              <p className="text-white font-semibold mb-2">💳 Paiements simples et rapides</p>
              <ul className="space-y-1 text-[#E8E8E8] text-sm">
                <li>• Retrait des gains via PayPal</li>
                <li>• Seuil minimum : <strong className="text-[#FF6B35]">50€</strong></li>
                <li>• Processus clair, sans complexité</li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* À qui s'adresse */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-16"
        >
          <h2 className="text-3xl font-bold text-white mb-8 text-center">
            À qui s'adresse le programme vendeur ?
          </h2>
          <div className="bg-gradient-to-br from-[#1A1A1D] to-[#121214] p-8 rounded-xl border border-white/10">
            <p className="text-[#B8B8BA] mb-6 text-center">
              Ce programme est idéal si vous êtes :
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto">
              {[
                'Actif sur les réseaux sociaux',
                'En contact avec des particuliers',
                'Déjà sollicité pour des recommandations IPTV',
                'Motivé à développer un revenu complémentaire ou principal'
              ].map((item, index) => (
                <div key={index} className="flex items-start space-x-3 bg-[#222226] p-4 rounded-lg">
                  <Check className="h-5 w-5 text-[#00FF88] mt-0.5 flex-shrink-0" />
                  <span className="text-white">{item}</span>
                </div>
              ))}
            </div>
            <p className="text-center text-[#00FF88] font-semibold mt-6 text-lg">
              👉 Aucune compétence technique requise.
            </p>
          </div>
        </motion.div>

        {/* Ce que vous n'avez PAS à gérer */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mb-16"
        >
          <div className="bg-gradient-to-br from-[#00FF88]/10 to-[#00FFFF]/10 p-8 rounded-xl border border-[#00FF88]/30">
            <h3 className="text-2xl font-bold text-white mb-6 text-center">
              Ce que vous n'avez <span className="text-[#00FF88]">PAS</span> à gérer
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 max-w-5xl mx-auto">
              {notYourJob.map((item, index) => (
                <div
                  key={index}
                  className="bg-[#222226] p-4 rounded-lg border border-[#00FF88]/20 text-center"
                >
                  <p className="text-white text-sm">{item}</p>
                </div>
              ))}
            </div>
            <p className="text-center text-[#00FF88] font-bold mt-6 text-xl">
              IPTV4KFR s'occupe de tout.
            </p>
          </div>
        </motion.div>

        {/* Formulaire d'inscription */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mb-16"
        >
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-white mb-4">
                Candidature – Devenir vendeur IPTV4KFR
              </h2>
              <p className="text-[#B8B8BA]">
                L'inscription est simple et rapide.
              </p>
            </div>

            {success ? (
              <div className="bg-[#00FF88]/10 border border-[#00FF88] rounded-xl p-8 text-center">
                <CheckCircle className="h-16 w-16 text-[#00FF88] mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-white mb-3">
                  Candidature envoyée avec succès !
                </h3>
                <p className="text-[#E8E8E8] mb-4">
                  Vérifiez votre email pour confirmer votre adresse.
                  Votre compte sera activé après approbation.
                </p>
                <p className="text-[#B8B8BA] text-sm">
                  Redirection vers l'accueil...
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-gradient-to-br from-[#1A1A1D] to-[#121214] p-8 rounded-xl border border-white/10">
                {error && (
                  <div className="bg-[#FF3B5C]/10 border border-[#FF3B5C] rounded-lg p-4 mb-6">
                    <p className="text-[#FF3B5C]">{error}</p>
                  </div>
                )}

                <div className="space-y-5">
                  <div>
                    <label className="block text-[#B8B8BA] text-sm mb-2">
                      Nom complet <span className="text-[#FF3B5C]">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.full_name}
                      onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                      className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#FF6B35] focus:outline-none"
                      placeholder="Votre nom complet"
                    />
                  </div>

                  <div>
                    <label className="block text-[#B8B8BA] text-sm mb-2">
                      Email <span className="text-[#FF3B5C]">*</span>
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#FF6B35] focus:outline-none"
                      placeholder="votre@email.com"
                    />
                    <div className="mt-2 bg-[#FFB800]/10 border border-[#FFB800]/30 rounded-lg p-3">
                      <p className="text-[#FFB800] text-xs font-medium">
                        L'adresse email utilis&eacute;e pour votre compte vendeur doit &ecirc;tre diff&eacute;rente de celle utilis&eacute;e pour un &eacute;ventuel compte client. Cela permet de s&eacute;parer les r&ocirc;les et d'&eacute;viter les conflits de commission.
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-[#B8B8BA] text-sm mb-2">
                      Mot de passe <span className="text-[#FF3B5C]">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        required
                        value={formData.password}
                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                        className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#FF6B35] focus:outline-none pr-12"
                        placeholder="Minimum 8 caractères"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B6B6E] hover:text-white"
                      >
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-[#B8B8BA] text-sm mb-2">
                      Confirmation du mot de passe <span className="text-[#FF3B5C]">*</span>
                    </label>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      required
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                      className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#FF6B35] focus:outline-none"
                      placeholder="Confirmer le mot de passe"
                    />
                  </div>

                  <div>
                    <label className="block text-[#B8B8BA] text-sm mb-2">
                      Motivation (optionnel)
                    </label>
                    <textarea
                      value={formData.motivation}
                      onChange={(e) => setFormData({...formData, motivation: e.target.value})}
                      rows="4"
                      className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#FF6B35] focus:outline-none resize-none"
                      placeholder="Pourquoi souhaitez-vous rejoindre la team IPTV4KFR ?"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-[#FF6B35] to-[#F7931E] text-white font-semibold py-4 rounded-lg hover:shadow-xl hover:shadow-[#FF6B35]/50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        <span>Envoi en cours...</span>
                      </>
                    ) : (
                      <span>Rejoindre la team IPTV4KFR</span>
                    )}
                  </button>

                  <p className="text-[#00FF88] text-sm text-center">
                    👉 Une fois inscrit, vous accédez directement à votre espace vendeur.
                  </p>
                </div>
              </form>
            )}
          </div>
        </motion.div>

        {/* Message de projection final */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-gradient-to-br from-[#FF6B35] to-[#F7931E] p-10 rounded-xl text-center"
        >
          <TrendingUp className="h-12 w-12 text-white mx-auto mb-6" />
          <h3 className="text-3xl font-bold text-white mb-4">
            Rejoindre IPTV4KFR, ce n'est pas seulement vendre un abonnement
          </h3>
          <p className="text-white text-lg max-w-3xl mx-auto">
            C'est intégrer une plateforme structurée, pensée pour la performance et le long terme.
            Faites partie d'une team qui grandit avec vous.
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default BecomeVendor;
